import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Leaf, Scan, Drill, Scissors, Activity, AlignCenter, ChevronRight, Mail, Phone } from 'lucide-react';
import { LeafCluster } from '../components/WoodlandSVG';
import { fadeUp, staggerContainer, staggerItem, pageTransition, viewportConfig } from '../components/AnimationVariants';
import { REFERRAL_EMAIL } from './referral/ReferralBase';

const TYPES = [
  {
    label: 'CBCT',
    to: '/referral/cbct',
    Icon: Scan,
    desc: 'Detailed 3D imaging to support diagnosis and treatment planning for implants, complex extractions, endodontics, impacted teeth, bone evaluation, and anatomical review.',
    detail: 'Small field, dual jaw, mandible or maxilla views available. Clear imaging and prompt sharing of scan data with referring clinicians.',
  },
  {
    label: 'Dental Implants',
    to: '/referral/dental-implants',
    Icon: Drill,
    desc: 'Single, multiple, or complex implant solutions. Treatment planning based on clinical assessment, CBCT imaging, bone evaluation, and restorative goals.',
    detail: 'Careful, predictable implant care with strong communication with the referring dentist throughout the process.',
  },
  {
    label: 'Extractions',
    to: '/referral/extractions',
    Icon: Scissors,
    desc: 'Referrals accepted for broken-down teeth, retained roots, impacted wisdom teeth, and cases requiring careful assessment due to anatomy or patient anxiety.',
    detail: 'Calm, clear, and supportive care — treatment fully explained before proceeding, with timely outcome reports.',
  },
  {
    label: 'Endodontics',
    to: '/referral/endodontics',
    Icon: Activity,
    desc: 'Root canal treatment, retreatment, complex anatomy, calcified canals, and diagnostic consultations supported by magnification and digital imaging.',
    detail: 'Focused on preserving natural teeth wherever possible, with clear communication between patient and referring dentist.',
  },
  {
    label: 'Orthodontics',
    to: '/referral/orthodontics',
    Icon: AlignCenter,
    desc: 'Referrals accepted for children, teenagers, and adults requiring orthodontic assessment, fixed braces, aligners, complex bite review, or multidisciplinary planning.',
    detail: 'Patients receive a detailed assessment, clear explanation of options, timelines, and retention needs.',
  },
];

export default function Referral() {
  return (
    <motion.div {...pageTransition} className="overflow-x-hidden pt-24">

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <motion.div className="absolute right-0 top-0 w-64 opacity-15 hidden md:block"
          animate={{ y: [0, -10, 0] }} transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}>
          <LeafCluster />
        </motion.div>
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <motion.p className="text-sage-light text-sm uppercase tracking-widest font-medium mb-4 flex items-center justify-center gap-2"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.1 }}>
            <Leaf className="w-4 h-4" /> For Dental Professionals
          </motion.p>
          <motion.h1 className="text-4xl md:text-5xl font-serif text-white font-semibold mb-5"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.2 }}>
            Specialist Referrals
          </motion.h1>
          <motion.p className="text-cream/75 text-lg leading-relaxed max-w-xl mx-auto mb-8"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.3 }}>
            Woodlands welcomes referrals from dentists who would like their patients to access focused support in CBCT imaging, implant planning and treatment, extractions, endodontics and specialist orthodontics. We will keep you informed, provide clear reports where appropriate, and return the patient to your care once the referred treatment has been completed.
          </motion.p>
          <motion.div className="flex flex-wrap gap-3 justify-center"
            variants={fadeUp} initial="hidden" animate="visible" transition={{ delay: 0.4 }}>
            <a href={`mailto:${REFERRAL_EMAIL}`} className="btn-outline-white text-sm py-2.5">
              <Mail className="w-4 h-4" /> {REFERRAL_EMAIL}
            </a>
            <a href="tel:+442088665556" className="btn-outline-white text-sm py-2.5">
              <Phone className="w-4 h-4" /> 020 8866 5556
            </a>
          </motion.div>
        </div>
      </section>

      {/* Referral type cards */}
      <section className="section-padding bg-cream">
        <div className="container-max">
          <div className="text-center mb-12">
            <motion.p className="section-subtitle justify-center"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <Leaf className="w-4 h-4" /> Choose a Specialty
            </motion.p>
            <motion.h2 className="section-title"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              Select Your Referral Type
            </motion.h2>
            <motion.p className="text-gray-500 mt-3 max-w-xl mx-auto"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              Each form is tailored to the clinical information we need. Complete it online and we'll be in touch to arrange the appointment.
            </motion.p>
          </div>

          <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {TYPES.map((type) => {
              const Icon = type.Icon;
              return (
                <motion.div key={type.to} variants={staggerItem}>
                  <Link to={type.to} className="group block h-full">
                    <motion.div
                      className="card-wood h-full p-6 flex flex-col"
                      whileHover={{ y: -4, boxShadow: '0 16px 40px rgba(45,90,61,0.15)' }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
                      <div className="w-12 h-12 rounded-xl bg-sage-pale flex items-center justify-center mb-4 group-hover:bg-forest transition-colors duration-300">
                        <Icon className="w-6 h-6 text-forest group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="font-serif text-forest text-xl font-semibold mb-2">{type.label}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-3 flex-1">{type.desc}</p>
                      <p className="text-gray-400 text-xs leading-relaxed mb-5">{type.detail}</p>
                      <div className="flex items-center gap-1 text-forest font-semibold text-sm group-hover:gap-2 transition-all">
                        Refer a patient <ChevronRight className="w-4 h-4" />
                      </div>
                    </motion.div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-10">
            <motion.p className="section-subtitle justify-center"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              <Leaf className="w-4 h-4" /> Simple Process
            </motion.p>
            <motion.h2 className="section-title"
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
              How Referrals Work
            </motion.h2>
          </div>
          <motion.div className="grid md:grid-cols-4 gap-6"
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            {[
              { step: '01', title: 'Choose a specialty', desc: 'Select the appropriate referral type above.' },
              { step: '02', title: 'Complete the form', desc: 'Fill in the clinical details and patient information.' },
              { step: '03', title: 'We get in touch', desc: 'Our team will contact you and the patient to confirm.' },
              { step: '04', title: 'Outcome report', desc: 'We send a detailed treatment report back to your practice.' },
            ].map((item) => (
              <motion.div key={item.step} variants={staggerItem} className="text-center">
                <div className="w-12 h-12 rounded-full bg-sage-pale flex items-center justify-center mx-auto mb-4">
                  <span className="font-serif text-forest font-bold text-sm">{item.step}</span>
                </div>
                <h4 className="font-serif text-forest font-semibold mb-2">{item.title}</h4>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="px-4 pb-16 bg-cream">
        <div className="container-max">
          <motion.div className="bg-forest rounded-3xl p-8 md:p-12 text-white text-center"
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={viewportConfig}>
            <h2 className="font-serif text-3xl font-semibold mb-3">Questions about referring?</h2>
            <p className="text-cream/75 mb-7 max-w-lg mx-auto">
              Our team is happy to discuss complex cases, urgency, or any aspect of the referral process before you submit.
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <a href="tel:+442088665556" className="btn-primary">
                <Phone className="w-4 h-4" /> Call 020 8866 5556
              </a>
              <a href={`mailto:${REFERRAL_EMAIL}`} className="btn-outline-white">
                <Mail className="w-4 h-4" /> Email Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
}
